<template>
    <div>
        <cic-popup
            id="edit_auto_attendant"
            :title="item.profileId ? 'Edit Auto Attendant' : 'New Auto Attendant'"
            :saveable="editing"
            class="no-padding"
            @blur="$emit('blur')"
            @input="$emit('input')"
        >
            <el-tabs>
                <el-tab-pane :label="$t('Menu Settings')">
                    <div class="item-form">
                        <ul>
                            <li class="form-label-first">
                                {{ $t('Profile Name') }}
                            </li>
                            <li>
                                <cic-textbox
                                    id="profile_name"
                                    :enabled="editing"
                                    :value="item.name"
                                    :maxlength="63"
                                    @input="(val) => updateFieldValue('name', val)"
                                />
                            </li>
                            <li class="form-label">
                                {{ $t('Description') }}
                            </li>
                            <li>
                                <cic-textbox
                                    id="profile_description"
                                    :enabled="editing"
                                    :value="item.description"
                                    :maxlength="255"
                                    @input="(val) => updateFieldValue('description', val)"
                                />
                            </li>
                            <li class="form-label">
                                {{ $t('Media List') }}
                            </li>
                            <li>
                                <cic-select
                                    id="profile_media_list"
                                    :enabled="editing"
                                    :options="
                                        $store.routeData.mediaLists.map((x) => ({
                                            value: x.mediaListId,
                                            label: x.name,
                                        }))
                                    "
                                    :value="item.mediaListId"
                                    :placeholder="$t('Please select...')"
                                    @input="(val) => updateFieldValue('mediaListId', val)"
                                />
                            </li>
                            <li v-if="warning">
                                {{ $t('* Selected media list is restricted') }}
                            </li>
                            <li class="form-label">{{ $t('Introduction Prompt') }}</li>
                            <li>
                                <cic-select
                                    id="profile_intro_prompt"
                                    :enabled="editing && !warning"
                                    :options="prompts.map((x) => ({ value: x.id, label: x.name }))"
                                    :placeholder="$t('Please select...')"
                                    :value="item.introPromptId"
                                    @input="(val) => updateFieldValue('introPromptId', val)"
                                />
                            </li>
                            <li class="form-label">{{ $t('No Response Timeout') }}</li>
                            <li>
                                <cic-select
                                    id="profile_no_response_timeout"
                                    :enabled="editing"
                                    :options="
                                        [3, 4, 5, 6, 7, 8, 9, 10].map((x) => ({
                                            value: x,
                                            label: $t('[x] seconds').replace(/\[x]/g, x.toString()),
                                        }))
                                    "
                                    :value="item.timeout"
                                    @input="(val) => updateFieldValue('timeout', val)"
                                />
                            </li>
                            <li class="form-label">{{ $t('No Response Prompt') }}</li>
                            <li>
                                <cic-select
                                    id="profile_no_response_prompt"
                                    :enabled="editing && !warning"
                                    :options="prompts.map((x) => ({ value: x.id, label: x.name }))"
                                    :placeholder="$t('Please select...')"
                                    :value="item.noResponsePromptId"
                                    clearable
                                    @input="(val) => updateFieldValue('noResponsePromptId', val)"
                                />
                            </li>
                            <li class="form-label">{{ $t('Invalid Response Prompt') }}</li>
                            <li>
                                <cic-select
                                    id="profile_invalid_response_prompt"
                                    :enabled="editing && !warning"
                                    :options="prompts.map((x) => ({ value: x.id, label: x.name }))"
                                    :placeholder="$t('Please select...')"
                                    :value="item.invalidPromptId"
                                    clearable
                                    @input="(val) => updateFieldValue('invalidPromptId', val)"
                                />
                            </li>
                            <li class="form-label">{{ $t('Maximum Attempts') }}</li>
                            <li>
                                <cic-textbox
                                    id="profile_max_attempts"
                                    :enabled="editing"
                                    :value="item.maxRetries"
                                    int-only
                                    @input="(val) => updateFieldValue('maxRetries', val)"
                                />
                            </li>
                            <li class="form-label">{{ $t('Select a service to route to if no response') }}</li>
                            <li>
                                <span>&nbsp;-&gt; </span>
                                <RouteLabel
                                    :route="noResponse ? noResponse.route : null"
                                    @click="openRoute('noresponse')"
                                />
                            </li>
                        </ul>
                    </div>
                </el-tab-pane>
                <el-tab-pane :label="$t('Menu Options')">
                    <table class="cic-table">
                        <thead>
                            <tr>
                                <th>{{ $t('Key') }}</th>
                                <th>{{ $t('Option Name') }}</th>
                                <th>{{ $t('Menu Route') }}</th>
                                <th v-if="editing" class="td-checkbox"></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="itemRoute in routes">
                                <td>
                                    <cic-button @click="openRoute(itemRoute.key)">{{ itemRoute.key }}</cic-button>
                                </td>
                                <td>
                                    <cic-textbox
                                        :enabled="editing"
                                        :value="itemRoute.label"
                                        @input="(val) => updateRouteLabel(itemRoute.key, val)"
                                    />
                                </td>
                                <td>
                                    <RouteLabel :route="itemRoute.route" @click="openRoute(itemRoute.key)" />
                                </td>
                                <td v-if="editing">
                                    <icon-button icon="remove-icon" @click="removeRoute(itemRoute.key)" />
                                </td>
                            </tr>
                            <tr v-if="editing">
                                <td colspan="3"></td>
                                <td><icon-button icon="add-icon" @click="showKeypad = true" /></td>
                            </tr>
                        </tbody>
                    </table>
                </el-tab-pane>
            </el-tabs>
        </cic-popup>

        <KeyPad
            v-if="showKeypad"
            :keys="item.routes.map((x) => x.key)"
            class="no-padding"
            @blur="showKeypad = false"
            @click="openRoute"
        />

        <ServiceSelector
            v-if="route"
            :editing="editing"
            :route="route"
            @change="updateRouteValue"
            @input="updateRoute"
            @blur="handleBlur"
            @remove="removeRoute(routeKey)"
        />
    </div>
</template>

<script>
    import { ElTabs, ElTabPane } from 'element-plus';
    import CicButton from '@/components/CicButton';
    import CicPopup from '@/components/CicPopup';
    import IconButton from '@/components/controls/IconButton';
    import CicSelect from '@/components/inputs/CicSelect';
    import CicTextbox from '@/components/inputs/CicTextbox';
    import { getRoute, getUpdatedRoute } from '@/utils/RouteCollectionUtils';
    import ServiceSelector from '../../serviceSelector/ServiceSelector';
    import KeyPad from './KeyPad';
    import RouteLabel from './RouteLabel';

    export default {
        name: 'EditAutoAttendant',
        components: {
            CicButton,
            CicPopup,
            CicSelect,
            CicTextbox,
            IconButton,
            KeyPad,
            RouteLabel,
            ServiceSelector,
            'el-tabs': ElTabs,
            'el-tab-pane': ElTabPane,
        },
        props: {
            item: {
                type: Object,
                required: true,
            },
        },
        emits: ['blur', 'change', 'changeList', 'input'],
        data() {
            return {
                editing: false,
                route: null,
                routeKey: null,
                showKeypad: false,
            };
        },
        computed: {
            noResponse() {
                return this.item.routes.find((x) => x.key === 'noresponse');
            },
            prompts() {
                const list = this.$store.routeData.mediaLists.find((x) => x.mediaListId === this.item.mediaListId);
                return list?.prompts ?? [];
            },
            routes() {
                const keys = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'];
                return this.item.routes
                    .filter((x) => x.key !== 'noresponse')
                    .sort((a, b) => (keys.indexOf(a.key) > keys.indexOf(b.key) ? 1 : -1));
            },
            warning() {
                return (
                    this.item.mediaListId &&
                    this.$store.routeData.mediaLists.some((x) => x.mediaListId === this.item.mediaListId && !x.allowed)
                );
            },
        },
        created() {
            this.editing = !this.item.profileId || this.$store.hasRight('EDIT_AUTO_ATTENDANTS');
        },
        methods: {
            handleBlur() {
                this.route = null;
                this.routeKey = null;
            },

            openRoute(key) {
                this.showKeypad = false;
                let route = this.item.routes.find((x) => x.key === key);
                if (route) {
                    this.routeKey = key;
                    this.route = { ...route.route };
                } else if (this.editing) {
                    this.routeKey = key;
                    this.route = getRoute(1);
                }
            },

            removeRoute(key) {
                this.route = null;
                this.routeKey = null;
                const index = this.item.routes.findIndex((x) => x.key === key);
                if (index >= 0) {
                    const routes = [...this.item.routes];
                    routes.splice(index, 1);
                    this.updateFieldValue('routes', routes);
                }
            },

            updateFieldValue(field, val) {
                this.$emit('change', field, val);
            },

            updateRoute() {
                const routes = [...this.item.routes];
                const index = this.item.routes.findIndex((x) => x.key === this.routeKey);
                if (index >= 0) {
                    routes[index].route = this.route;
                } else {
                    routes.push({ route: this.route, key: this.routeKey });
                }

                this.route = null;
                this.routeKey = null;
                this.updateFieldValue('routes', routes);
            },

            updateRouteLabel(key, val) {
                const index = this.item.routes.findIndex((x) => x.key === key);
                const routes = [...this.item.routes];
                routes[index].label = val;
                this.updateFieldValue('routes', routes);
            },

            updateRouteValue(field, val) {
                this.route = getUpdatedRoute(this.route, field, val);
                if (
                    (this.route.type === 6 && field === 'mappingType') ||
                    ([1, 2].includes(this.route.type) && field === 'mediaListId')
                ) {
                    this.$emit('changeList', val);
                }
            },
        },
    };
</script>
